import React, { useState, useEffect } from 'react';
import { X, Star, ArrowRight, Brain, Sparkles } from 'lucide-react';
import { Database } from '../lib/supabase';
import Logo from './Logo';

type AIActivity = Database['public']['Tables']['ai_generated_activities']['Row'];

export interface AIActivitySimulatorProps {
  activity: AIActivity;
  onComplete: (score: number, timeSpent: number) => void;
  onClose: () => void;
}

interface Question {
  question: string;
  options: string[];
  correct: number;
  explanation?: string;
}

interface VisualElement {
  type: 'illustration' | 'diagram' | 'counting_objects' | 'number_line' | 'shape_grid' | 'pattern' | 'chart';
  description: string;
  alt_text: string;
  count?: number;
  items?: string[];
  colors?: string[];
  shapes?: string[];
  size?: 'small' | 'medium' | 'large';
}

interface LearningMaterial {
  concept: string;
  introduction: string;
  examples?: string[];
  vocabulary?: { term: string; definition: string }[];
  visuals?: VisualElement[];
}

export function AIActivitySimulator({ activity, onComplete, onClose }: AIActivitySimulatorProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [startTime] = useState(Date.now());
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showLearningMaterial, setShowLearningMaterial] = useState(true);
  const [learningMaterial, setLearningMaterial] = useState<LearningMaterial | null>(null);

  useEffect(() => {
    // Safety check for activity data
    if (!activity) {
      console.warn('AIActivitySimulator: activity prop is undefined or null');
      return;
    }

    // Parse questions and learning material from activity content
    if (activity.content && typeof activity.content === 'object') {
      const content = activity.content as Record<string, unknown>;
      if (Array.isArray(content.questions)) {
        setQuestions(content.questions as Question[]);
      }
      
      if (content.learning_material && typeof content.learning_material === 'object') {
        setLearningMaterial(content.learning_material as LearningMaterial);
      } else {
        // If no learning material is provided, skip directly to questions
        setShowLearningMaterial(false);
      }
    }
  }, [activity]);

  // Safety check for activity data
  if (!activity) {
    return null;
  }

  const handleStartQuestions = () => {
    setShowLearningMaterial(false);
  };

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNextQuestion = () => {
    const isCorrect = selectedAnswer === questions[currentQuestion]?.correct;
    
    if (isCorrect) {
      setScore(prev => prev + Math.floor(100 / questions.length));
    }

    // Show explanation if available
    if (questions[currentQuestion]?.explanation) {
      setShowExplanation(true);
      setTimeout(() => {
        setShowExplanation(false);
        proceedToNext();
      }, 2000);
    } else {
      proceedToNext();
    }
  };

  const proceedToNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      // Activity completed
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      setShowResult(true);
      
      // Call onComplete after a short delay to show the result
      setTimeout(() => {
        onComplete(score, timeSpent);
      }, 2000);
    }
  };

  if (questions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading AI activity...</p>
        </div>
      </div>
    );
  }

  // Show learning material first if available
  if (showLearningMaterial && learningMaterial) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl max-w-2xl w-full mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Logo size="lg" variant="icon-only" />
                <div>
                  <h2 className="text-xl font-bold">{activity.title}</h2>
                  <div className="flex items-center space-x-2 text-blue-100">
                    <Brain className="w-4 h-4" />
                    <span>Learning Time!</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Learning Content */}
          <div className="p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {learningMaterial.concept}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                {learningMaterial.introduction}
              </p>
            </div>

            {/* Examples */}
            {learningMaterial.examples && learningMaterial.examples.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Examples:</h4>
                <div className="bg-primary-50 rounded-lg p-4">
                  <ul className="space-y-2">
                    {learningMaterial.examples.map((example, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Star className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Vocabulary */}
            {learningMaterial.vocabulary && learningMaterial.vocabulary.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Key Words:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {learningMaterial.vocabulary.map((vocab, index) => (
                    <div key={index} className="bg-purple-50 rounded-lg p-3">
                      <h5 className="font-semibold text-purple-800">{vocab.term}</h5>
                      <p className="text-sm text-gray-700">{vocab.definition}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visuals */}
            {learningMaterial.visuals && learningMaterial.visuals.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Visual Learning:</h4>
                <div className="space-y-3">
                  {learningMaterial.visuals.map((visual, index) => (
                    <div key={index} className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
                      <p className="text-gray-700 italic">
                        <strong>Imagine:</strong> {visual.description}
                      </p>
                      {visual.items && visual.items.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {visual.items.map((item, itemIndex) => (
                            <span key={itemIndex} className="bg-green-200 text-green-800 px-2 py-1 rounded text-sm">
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Button */}
            <div className="text-center">
              <button
                onClick={handleStartQuestions}
                className="flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity mx-auto"
              >
                <Brain className="w-5 h-5" />
                <span>Let's Practice! ({questions.length} Questions)</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showExplanation) {
    const currentQ = questions[currentQuestion];
    const isCorrect = selectedAnswer === currentQ.correct;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-4">
            <Logo size="xl" variant="icon-only" />
          </div>
          
          <h3 className={`text-xl font-bold mb-2 ${
            isCorrect ? 'text-green-700' : 'text-red-700'
          }`}>
            {isCorrect ? 'Correct!' : 'Not quite right'}
          </h3>
          
          {currentQ.explanation && (
            <p className="text-gray-600 mb-4">{currentQ.explanation}</p>
          )}
          
          <div className="text-sm text-gray-500">
            Moving to next question...
          </div>
        </div>
      </div>
    );
  }

  if (showResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            <div className="mx-auto mb-4">
              <Logo size="xl" variant="icon-only" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {score >= 75 ? 'Excellent!' : score >= 50 ? 'Good Job!' : 'Keep Practicing!'}
            </h2>
            <p className="text-gray-600">You scored {score} out of 100 points!</p>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Brain className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">AI Activity Complete!</h3>
            </div>
            <div className="flex justify-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span>{activity.points_reward} points earned</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentProblem = questions[currentQuestion];
  if (!currentProblem) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Logo size="lg" variant="icon-only" />
              <div>
                <h2 className="text-xl font-bold">{activity.title}</h2>
                <div className="flex items-center space-x-2 text-blue-100">
                  <Sparkles className="w-4 h-4" />
                  <span>AI-Generated Activity</span>
                  <span>•</span>
                  <span>Question {currentQuestion + 1} of {questions.length}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {currentProblem.question}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {currentProblem.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                className={`p-4 rounded-lg border-2 text-left font-medium transition-all duration-200 ${
                  selectedAnswer === index
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                    selectedAnswer === index
                      ? 'border-purple-500 bg-purple-500 text-white'
                      : 'border-gray-300 text-gray-500'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <span>{option}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Score: {score} / 100
            </div>
            
            <button
              onClick={handleNextQuestion}
              disabled={selectedAnswer === null}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              <span>{currentQuestion < questions.length - 1 ? 'Next' : 'Finish'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}